import { z } from "@hono/zod-openapi";

/**
 * The one wire shape for LLM usage (cost + tokens) on any subject — agent,
 * agent version, thread, eval run, processor run, workspace. Computed by the
 * `@quovy/db` usage helpers; rendered by `UsageSummary` / `UsageBreakdown`.
 * Registered as OpenAPI components here so the API and web share one schema.
 */

/**
 * What `GET /internal/dashboard/usage?subject=` totals, per public id.
 * `agent_evals` / `agent_version_evals` count only eval-run spend.
 */
export const USAGE_SUBJECTS = [
  "agent",
  "agent_version",
  "agent_thread",
  "agent_eval_run",
  "agent_evals",
  "agent_version_evals",
  "extraction_run",
  "classification_run",
  "workflow_run",
] as const;
export type UsageSubjectType = (typeof USAGE_SUBJECTS)[number];

export const USAGE_BREAKDOWN_DIMENSIONS = [
  "agent",
  "agent_thread",
  "agent_eval_run",
  "model",
  "source",
] as const;
export type UsageBreakdownDimension = (typeof USAGE_BREAKDOWN_DIMENSIONS)[number];

/** Dimensions a breakdown scoped to one agent supports; `agent` and `source` span other spend. */
export const AGENT_USAGE_BREAKDOWN_DIMENSIONS = [
  "agent_thread",
  "agent_eval_run",
  "model",
] as const satisfies readonly UsageBreakdownDimension[];

export const USAGE_BREAKDOWN_DIMENSION_LABELS: Record<UsageBreakdownDimension, string> = {
  agent: "Agent",
  agent_thread: "Thread",
  agent_eval_run: "Eval run",
  model: "Model",
  source: "Source",
};

export const USAGE_WINDOWS = ["30d", "all"] as const;
export type UsageWindow = (typeof USAGE_WINDOWS)[number];

export const USAGE_WINDOW_LABELS: Record<UsageWindow, string> = {
  "30d": "30 days",
  all: "All time",
};

const usageShape = {
  /** Decimal string at full numeric precision. */
  cost_usd: z.string(),
  /** Inclusive of `cached_input_tokens` and `cache_write_tokens`. */
  input_tokens: z.number().int(),
  /** Inclusive of `reasoning_tokens` on providers that report the split. */
  output_tokens: z.number().int(),
  cached_input_tokens: z.number().int(),
  cache_write_tokens: z.number().int(),
  reasoning_tokens: z.number().int(),
  call_count: z.number().int(),
  last_activity_at: z.string().nullable(),
};

export const UsageSchema = z.object(usageShape).openapi("Usage");
export type UsageTotals = z.infer<typeof UsageSchema>;

export const USAGE_RESOURCE_DEFINITIONS = [
  {
    type: "agent_thread",
    prefix: "ath",
    dependencies: [],
    contributors: ["model"],
    coverage: "partial",
  },
  {
    type: "agent",
    prefix: "agt",
    dependencies: [
      { type: "agent_thread", relationship: "Threads run by this agent" },
      { type: "agent_eval_run", relationship: "Eval runs for this agent" },
    ],
    contributors: ["agent_thread", "agent_eval_run", "model"],
    agentThreadFilters: true,
    coverage: "partial",
  },
  {
    type: "agent_version",
    prefix: "agv",
    dependencies: [
      { type: "agent_thread", relationship: "Threads pinned to this version" },
      { type: "agent_eval_run", relationship: "Eval runs pinned to this version" },
    ],
    contributors: ["agent_thread", "agent_eval_run", "model"],
    agentThreadFilters: true,
    coverage: "partial",
  },
  {
    type: "agent_eval_run",
    prefix: "aer",
    dependencies: [
      { type: "agent_thread", relationship: "Threads dispatched by this eval run" },
      { type: "agent_eval_result", relationship: "Results produced by this eval run" },
    ],
    contributors: ["agent_eval_result", "agent_thread", "model"],
    coverage: "partial",
  },
  {
    type: "agent_eval_result",
    prefix: "axr",
    dependencies: [{ type: "agent_thread", relationship: "Thread that produced this result" }],
    contributors: ["model"],
    coverage: "partial",
  },
  {
    type: "agent_eval_scenario",
    prefix: "aes",
    dependencies: [
      { type: "agent_eval_result", relationship: "Results for this scenario" },
      { type: "agent_thread", relationship: "Threads that produced those results" },
    ],
    contributors: ["agent_eval_result", "agent_thread", "model"],
    coverage: "partial",
  },
  {
    type: "task",
    prefix: "tsk",
    dependencies: [{ type: "agent_thread", relationship: "Agent runs for this task" }],
    contributors: ["agent_thread", "model"],
    coverage: "partial",
  },
  {
    type: "workflow_run",
    prefix: "wfr",
    dependencies: [
      { type: "agent_thread", relationship: "Agent steps awaited by this run" },
      { type: "extraction_run", relationship: "Extraction steps in this run" },
      { type: "classification_run", relationship: "Classification steps in this run" },
    ],
    contributors: ["source", "model"],
    coverage: "partial",
  },
  {
    type: "workflow_definition",
    prefix: "wfd",
    dependencies: [{ type: "workflow_run", relationship: "Runs of this definition" }],
    contributors: ["workflow_run", "source", "model"],
    coverage: "partial",
  },
  {
    type: "workflow_definition_version",
    prefix: "wdv",
    dependencies: [
      { type: "workflow_run", relationship: "Runs pinned to this definition version" },
    ],
    contributors: ["workflow_run", "source", "model"],
    coverage: "partial",
  },
  {
    type: "extraction_run",
    prefix: "exr",
    dependencies: [],
    contributors: ["source", "model"],
    coverage: "complete",
  },
  {
    type: "extractor",
    prefix: "ext",
    dependencies: [{ type: "extraction_run", relationship: "Runs of this extractor" }],
    contributors: ["extraction_run", "source", "model"],
    coverage: "complete",
  },
  {
    type: "extractor_version",
    prefix: "exv",
    dependencies: [
      { type: "extraction_run", relationship: "Runs pinned to this extractor version" },
    ],
    contributors: ["extraction_run", "source", "model"],
    coverage: "complete",
  },
  {
    type: "classification_run",
    prefix: "clr",
    dependencies: [],
    contributors: ["source", "model"],
    coverage: "complete",
  },
  {
    type: "classifier",
    prefix: "cls",
    dependencies: [{ type: "classification_run", relationship: "Runs of this classifier" }],
    contributors: ["classification_run", "source", "model"],
    coverage: "complete",
  },
  {
    type: "classifier_version",
    prefix: "clv",
    dependencies: [
      { type: "classification_run", relationship: "Runs pinned to this classifier version" },
    ],
    contributors: ["classification_run", "source", "model"],
    coverage: "complete",
  },
  {
    type: "workspace",
    prefix: "wsp",
    dependencies: [
      { type: "user", relationship: "Users who initiated usage in this workspace" },
      { type: "agent_thread", relationship: "Agent threads in this workspace" },
      { type: "extraction_run", relationship: "Extraction runs in this workspace" },
      { type: "classification_run", relationship: "Classification runs in this workspace" },
      { type: "integration", relationship: "Billable integrations in this workspace" },
    ],
    contributors: [
      "user",
      "agent",
      "task",
      "agent_thread",
      "extraction_run",
      "classification_run",
      "integration",
      "source",
      "model",
    ],
    coverage: "complete",
  },
  {
    type: "organization",
    prefix: "org",
    dependencies: [{ type: "workspace", relationship: "Workspaces in this organization" }],
    contributors: ["workspace", "source", "model"],
    coverage: "complete",
  },
  {
    type: "user",
    prefix: "usr",
    dependencies: [
      { type: "workspace", relationship: "Workspaces where this user initiated usage" },
      { type: "agent_thread", relationship: "Threads directly initiated by this user" },
      { type: "workflow_run", relationship: "Workflow runs directly initiated by this user" },
      { type: "extraction_run", relationship: "Extraction runs directly initiated by this user" },
      {
        type: "classification_run",
        relationship: "Classification runs directly initiated by this user",
      },
    ],
    contributors: [
      "workspace",
      "agent_thread",
      "workflow_run",
      "extraction_run",
      "classification_run",
      "source",
      "model",
    ],
    coverage: "initiator_only",
  },
  {
    type: "integration",
    prefix: "int",
    dependencies: [],
    contributors: ["source"],
    coverage: "complete",
  },
] as const;

export type UsageResourceType = (typeof USAGE_RESOURCE_DEFINITIONS)[number]["type"];
export const USAGE_RESOURCE_TYPES = USAGE_RESOURCE_DEFINITIONS.map((entry) => entry.type) as [
  UsageResourceType,
  ...UsageResourceType[],
];
export const USAGE_BREAKDOWN_RESOURCE_TYPES = USAGE_RESOURCE_TYPES;
export type UsageBreakdownResourceType = UsageResourceType;
export const USAGE_HORIZONS = ["24h", "7d", "30d", "90d", "all"] as const;
export type UsageHorizon = (typeof USAGE_HORIZONS)[number];
export const USAGE_HORIZON_LABELS: Record<UsageHorizon, string> = {
  "24h": "24 hours",
  "7d": "7 days",
  "30d": "30 days",
  "90d": "90 days",
  all: "All time",
};
export const USAGE_FIDELITIES = ["auto", "hour", "day", "week", "month"] as const;
export type UsageFidelity = (typeof USAGE_FIDELITIES)[number];
export const USAGE_RESOLVED_FIDELITIES = ["hour", "day", "week", "month"] as const;
export type UsageResolvedFidelity = (typeof USAGE_RESOLVED_FIDELITIES)[number];
export const USAGE_HORIZON_FIDELITIES = {
  "24h": ["hour", "day"],
  "7d": ["hour", "day"],
  "30d": ["day", "week"],
  "90d": ["day", "week", "month"],
  all: ["month"],
} as const satisfies Record<UsageHorizon, readonly UsageResolvedFidelity[]>;
export const USAGE_THREAD_KINDS = ["all", "eval", "non_eval"] as const;
export type UsageThreadKind = (typeof USAGE_THREAD_KINDS)[number];
export const USAGE_CONTRIBUTOR_DIMENSIONS = [
  "workspace",
  "user",
  "agent",
  "task",
  "agent_thread",
  "agent_eval_run",
  "agent_eval_result",
  "workflow_run",
  "extraction_run",
  "classification_run",
  "integration",
  "model",
  "source",
] as const;
export type UsageContributorDimension = (typeof USAGE_CONTRIBUTOR_DIMENSIONS)[number];

export const UsageBatchSchema = z
  .object({
    subject: z.enum(USAGE_SUBJECTS),
    window: z.enum(USAGE_WINDOWS),
    /** One entry per requested id; `usage` is null when nothing was logged. */
    items: z.array(z.object({ id: z.string(), usage: UsageSchema.nullable() })),
  })
  .openapi("UsageBatch");
export type UsageBatch = z.infer<typeof UsageBatchSchema>;

const UsageBreakdownRowSchema = z.object({
  ...usageShape,
  /** Per-dimension key: a public id, a model name, or `source_type:component`. */
  key: z.string(),
  resource: z
    .object({
      type: z.enum(USAGE_BREAKDOWN_RESOURCE_TYPES),
      id: z.string(),
      name: z.string().nullable(),
    })
    .nullable(),
  /** The owning agent for thread and eval-run rows. */
  agent: z.object({ id: z.string(), name: z.string() }).nullable(),
  /** The object that launched a thread, or the immediate parent of a subtask. */
  parent: z.object({ type: z.string(), id: z.string(), name: z.string().nullable() }).nullable(),
  model: z.string().nullable(),
  source_type: z.string().nullable(),
  component: z.string().nullable(),
});
export type UsageBreakdownRow = z.infer<typeof UsageBreakdownRowSchema>;

export const USAGE_COVERAGE_LEVELS = ["complete", "partial", "initiator_only"] as const;
export type UsageCoverageLevel = (typeof USAGE_COVERAGE_LEVELS)[number];

export const UsageTimelineBucketSchema = z
  .object({ from: z.string(), to: z.string(), ...usageShape })
  .omit({ last_activity_at: true });
export type UsageTimelineBucket = z.infer<typeof UsageTimelineBucketSchema>;

export const UsageResourceMetadataSchema = z.object({
  id: z.string(),
  type: z.enum(USAGE_RESOURCE_TYPES),
  name: z.string(),
  workspace_id: z.string().nullable(),
  coverage: z.object({
    level: z.enum(USAGE_COVERAGE_LEVELS),
    warning: z.string().nullable(),
  }),
  capabilities: z.object({
    timeline: z.literal(true),
    agent_thread_filters: z.boolean(),
    contributor_dimensions: z.array(z.enum(USAGE_CONTRIBUTOR_DIMENSIONS)),
    default_contributor_dimension: z.enum(USAGE_CONTRIBUTOR_DIMENSIONS),
  }),
  dependencies: z.array(z.object({ type: z.enum(USAGE_RESOURCE_TYPES), relationship: z.string() })),
});
export type UsageResourceMetadata = z.infer<typeof UsageResourceMetadataSchema>;

export const UsagePageSchema = z.object({
  resource: UsageResourceMetadataSchema,
  usage: UsageSchema.nullable(),
  timeline: z.object({
    horizon: z.enum(USAGE_HORIZONS),
    fidelity: z.enum(USAGE_RESOLVED_FIDELITIES),
    buckets: z.array(UsageTimelineBucketSchema),
  }),
  contributors: z.object({
    group_by: z.enum(USAGE_CONTRIBUTOR_DIMENSIONS),
    limit: z.number().int(),
    truncated: z.boolean(),
    rows: z.array(UsageBreakdownRowSchema),
  }),
  filters: z.object({
    thread_kind: z.enum(USAGE_THREAD_KINDS),
    agent_eval_run_id: z.string().nullable(),
  }),
});
export type UsagePage = z.infer<typeof UsagePageSchema>;

export const UsageBreakdownSchema = z
  .object({
    group_by: z.enum(USAGE_BREAKDOWN_DIMENSIONS),
    window: z.enum(USAGE_WINDOWS),
    limit: z.number().int(),
    truncated: z.boolean(),
    rows: z.array(UsageBreakdownRowSchema),
  })
  .openapi("UsageBreakdown");
export type UsageBreakdown = z.infer<typeof UsageBreakdownSchema>;
